import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAnchorGroupProject, useMyProjects } from "~/api/queries";
import type { Group } from "~/api/types";
import { Select } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";

/**
 * Anchor (or clear) the project a team's access rides on. The owner delta ported from
 * `GroupDialogs.tsx`'s `AnchorProjectDialog`: the option list is `projects.mine`, not
 * `projects.list` — you can only anchor to a project you can already reach.
 */
export function AnchorProjectDialog({ group, onClose }: { group: Group; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: projects } = useMyProjects();
  const anchorGroup = useAnchorGroupProject();
  const [projectId, setProjectId] = useState("");

  const submit = () => {
    anchorGroup.mutate(
      { groupId: group.id, projectId: projectId || null },
      {
        onSuccess: () => {
          onClose();
          toast.success(t("Team project anchor updated"));
        },
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Anchor to a project")}
      description={group.name}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={anchorGroup.isPending} onClick={submit}>
            {t("Save")}
          </Button>
        </>
      }
    >
      <Select
        label={t("Project")}
        fullWidth
        value={projectId}
        onChange={(event) => setProjectId(event.target.value)}
      >
        <option value="">{t("No project")}</option>
        {(projects ?? []).map((project) => (
          <option key={project.id} value={project.id}>
            {project.name}
          </option>
        ))}
      </Select>
    </Dialog>
  );
}
